import type { ReactNode } from "react";

import { CLUSTER_PALETTE } from "./portfolioClusterStyles";
import {
  DEFAULT_ACCOUNT_HEALTH_THRESHOLDS,
  DEFAULT_BUSINESS_HEALTH_THRESHOLDS,
  formatAccountHealthBandLegend,
  formatBusinessHealthBandLegend,
  normalizeAccountHealthThresholds,
  normalizeBusinessHealthThresholds,
  type AccountHealthThresholds,
  type BusinessHealthThresholds,
} from "../../Utils/HealthThresholds";
import { BUSINESS_HEALTH_COLORS } from "../../Utils/Helper/businessHealth";

const LegendDot = ({ color }: { color: string }) => (
  <span
    className="mt-px size-2 shrink-0 rounded-full"
    style={{ backgroundColor: color }}
    aria-hidden
  />
);

const LegendBand = ({
  color,
  children,
}: {
  color: string;
  children: ReactNode;
}) => (
  <div className="flex items-center gap-1.5 text-[12.5px] font-semibold leading-snug text-gray-800">
    <LegendDot color={color} />
    <span>{children}</span>
  </div>
);

/** Build BH/AH header tooltips from vendor health_score_thresholds (or defaults). */
export function buildHeaderInfoTooltips(
  bh?: Partial<BusinessHealthThresholds> | null,
  ah?: Partial<AccountHealthThresholds> | null
) {
  const bhLegend = formatBusinessHealthBandLegend(
    normalizeBusinessHealthThresholds(bh ?? DEFAULT_BUSINESS_HEALTH_THRESHOLDS)
  );
  const ahLegend = formatAccountHealthBandLegend(
    normalizeAccountHealthThresholds(ah ?? DEFAULT_ACCOUNT_HEALTH_THRESHOLDS)
  );
  return {
    businessHealth: (
      <div className="flex flex-col gap-2.5">
        <p className="m-0 text-[12.5px] font-normal leading-relaxed text-gray-700">
          External signal from public data briefings — captures the
          customer&apos;s financial trajectory, market position, growth
          momentum, and macro business signals. Brings the customer&apos;s world
          to the vendor&apos;s fingertips.
        </p>
        <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
            From public data briefings
          </div>
          <LegendBand color={BUSINESS_HEALTH_COLORS.concern}>
            {bhLegend.concern}
          </LegendBand>
          <LegendBand color={BUSINESS_HEALTH_COLORS.stable}>
            {bhLegend.stable}
          </LegendBand>
          <LegendBand color={BUSINESS_HEALTH_COLORS.strong}>
            {bhLegend.strong}
          </LegendBand>
        </div>
      </div>
    ),
    accountHealth: (
      <div className="flex flex-col gap-2.5">
        <p className="m-0 text-[12.5px] font-normal leading-relaxed text-gray-700">
          Composite score measuring how the customer is experiencing the vendor
          — product adoption, usage depth, support quality, satisfaction, and
          engagement cadence. Computed from enterprise KPIs ingested per
          account.
        </p>
        <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
            From enterprise KPIs
          </div>
          <LegendBand color={CLUSTER_PALETTE.triage.color}>
            {ahLegend.triage}
          </LegendBand>
          <LegendBand color={CLUSTER_PALETTE.defend.color}>
            {ahLegend.defend}
          </LegendBand>
          <LegendBand color={CLUSTER_PALETTE.grow.color}>
            {ahLegend.delegate}
          </LegendBand>
          <LegendBand color={CLUSTER_PALETTE.expand.color}>
            {ahLegend.expand}
          </LegendBand>
        </div>
      </div>
    ),
    relationships:
      "Your RL level and team's aggregate RL for this account, plus relationship count and health. My RL = your personal pairs; Team RL = all mapped sponsor pairs.",
  };
}

/** @deprecated Prefer buildHeaderInfoTooltips(vendor thresholds) — defaults only. */
export const HEADER_INFO_TOOLTIPS = buildHeaderInfoTooltips();

export const headerInfoIconClass =
  "inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-400 transition-colors hover:border-purple-300 hover:bg-purple-50 hover:text-purple-600 focus:outline-none focus-visible:border-purple-400 focus-visible:text-purple-600 data-[state=open]:border-purple-300 data-[state=open]:bg-purple-50 data-[state=open]:text-purple-600";

export const headerTooltipClass =
  "z-[9999] max-w-[320px] border border-gray-200 bg-white px-3 py-2.5 text-[12.5px] font-normal normal-case tracking-normal leading-relaxed text-gray-800 shadow-[0_4px_16px_rgba(0,0,0,0.12)]";
